import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, openAuthedFile } from "@/api/client";
import {
  DOCUMENT_KIND_LABELS,
  DOCUMENT_AUDIENCE_LABELS,
  DOCUMENT_STATUS_LABELS,
  type DocumentKind,
  type DocumentAudience,
  type DocumentStatus,
} from "@ckgo2/shared/types";
import { formatDate } from "@ckgo2/shared/utils";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import SignStatusBadge from "@/components/common/SignStatusBadge";
import { FileText, Download, Loader2 } from "lucide-react";

interface DocRow {
  id: string;
  title: string;
  number?: string | null;
  orderId?: string | null;
  kind: DocumentKind;
  audience: DocumentAudience;
  status: DocumentStatus;
  requiresSignature: boolean;
  r2Key?: string | null;
  createdAt: string;
  signedAt?: string | null;
  orderNumber?: string | null;
  customerFirstName?: string | null;
  customerLastName?: string | null;
}

interface FacetedDocs {
  data: DocRow[];
  total: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  success: true;
}

export default function DocumentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const qs = searchParams.toString();
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  // "Contracts" quick-filter: the contract register is the kind people reach for, so promote it to a
  // one-click preset on top of the generic Kind facet (which still offers every kind).
  const kindFilter = searchParams.get("kind");
  const setKindPreset = (kind: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (kind) next.set("kind", kind);
    else next.delete("kind");
    next.delete("page");
    setSearchParams(next);
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["documents", qs],
    queryFn: () => api.get<FacetedDocs>(`/api/admin/documents?${qs}`),
  });

  const rows = data?.data ?? [];
  const facets = data?.facets ?? {};

  const openPdf = async (id: string) => {
    setPdfBusy(id);
    try {
      await openAuthedFile(`/api/admin/documents/${id}/pdf`);
    } finally {
      setPdfBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documents</h1>
        <p className="mt-1 text-gray-600 dark:text-gray-400">
          Generated contracts, itineraries & packages — with e-signature status
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => setKindPreset(null)}
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              !kindFilter
                ? "bg-primary-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            All documents
          </button>
          <button
            onClick={() => setKindPreset("contract")}
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              kindFilter === "contract"
                ? "bg-primary-600 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            Contracts
          </button>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder="Search by title…"
        total={data?.total ?? 0}
        limit={data?.limit ?? 25}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title="Kind"
              paramKey="kind"
              buckets={facets.kind ?? []}
              labelFor={(v) => DOCUMENT_KIND_LABELS[v as DocumentKind] ?? v}
            />
            <FacetPanel
              title="Status"
              paramKey="status"
              buckets={facets.status ?? []}
              labelFor={(v) => DOCUMENT_STATUS_LABELS[v as DocumentStatus] ?? v}
            />
            <FacetPanel
              title="Audience"
              paramKey="audience"
              buckets={facets.audience ?? []}
              labelFor={(v) => DOCUMENT_AUDIENCE_LABELS[v as DocumentAudience] ?? v}
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {rows.length === 0 ? (
            <div className="flex h-48 flex-col items-center justify-center text-gray-500">
              <FileText className="mb-2 h-10 w-10 text-gray-300" />
              <p>No documents</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {["Document", "Order", "Customer", "Kind", "Status", "Created", "Signed", ""].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {r.number ? <span className="font-mono">{r.number}</span> : r.title}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {r.orderId ? (
                        <Link
                          to={`/orders/${r.orderId}`}
                          className="text-primary-600 dark:text-primary-400 font-mono hover:underline"
                        >
                          {r.orderNumber ?? "—"}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                      {[r.customerFirstName, r.customerLastName].filter(Boolean).join(" ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {DOCUMENT_KIND_LABELS[r.kind]}
                    </td>
                    <td className="px-4 py-3">
                      <SignStatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDate(r.createdAt)}</td>
                    <td className="px-4 py-3 text-xs">
                      {r.signedAt ? (
                        <span className="text-emerald-600 dark:text-emerald-400">
                          {formatDate(r.signedAt)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => openPdf(r.id)}
                        className="btn-ghost btn-sm"
                        disabled={pdfBusy === r.id || !r.r2Key}
                        title="Open PDF"
                      >
                        {pdfBusy === r.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
